import { useParams, Link } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useApi } from "@/lib/useApi";
import { formatDate, formatEUR, label } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useState } from "react";
import { toast } from "sonner";
import { ArrowLeft, Lock, Unlock, FileDown, FilePlus } from "lucide-react";

const KIND = {
  identita: "Documento d'identità", codice_fiscale: "Codice fiscale", visura: "Visura camerale",
  agreement_generated: "Accordo generato", agreement_signed: "Accordo firmato",
  integrative_generated: "Modulo integrativo", integrative_signed: "Modulo integrativo firmato",
};

export default function AdminResellerDetail() {
  const { id } = useParams();
  const api = useApi();
  const qc = useQueryClient();
  const [newPlan, setNewPlan] = useState({ product_id: "", type: "percentage", percent: 20, amount: 50, module_amounts: {}, tiers: [{ from: 0, to: 10, percent: 20 }, { from: 11, to: null, percent: 25 }] });

  const { data: r, isLoading } = useQuery({
    queryKey: ["admin-reseller", id],
    queryFn: async () => (await api.get(`/admin/resellers/${id}`)).data,
  });
  const { data: products = [] } = useQuery({
    queryKey: ["admin-products"],
    queryFn: async () => (await api.get("/admin/products")).data,
  });

  const assignedIds = (r?.products || []).map((p) => p.product_id);
  const [selectedIds, setSelectedIds] = useState(null);
  const effectiveIds = selectedIds ?? assignedIds;

  const toggleBlock = useMutation({
    mutationFn: async (blocked) => (await api.patch(`/admin/resellers/${id}/block`, { blocked })).data,
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["admin-reseller", id] }); toast.success("Stato aggiornato"); },
  });

  const saveAssignments = useMutation({
    mutationFn: async () => (await api.put(`/admin/resellers/${id}/products`, { product_ids: effectiveIds })).data,
    onSuccess: () => { setSelectedIds(null); qc.invalidateQueries({ queryKey: ["admin-reseller", id] }); toast.success("Prodotti assegnati"); },
  });

  const createPlan = useMutation({
    mutationFn: async () => {
      const config = newPlan.type === "percentage" ? { percent: Number(newPlan.percent) }
                   : newPlan.type === "fixed" ? { amount: Number(newPlan.amount), module_amounts: Object.fromEntries(Object.entries(newPlan.module_amounts).map(([k, v]) => [k, Number(v) || 0])) }
                   : { tiers: newPlan.tiers.map((t) => ({ from: Number(t.from), to: t.to ? Number(t.to) : null, percent: Number(t.percent) })) };
      return (await api.post("/admin/commission-plans", { reseller_id: id, product_id: newPlan.product_id, type: newPlan.type, config })).data;
    },
    onSuccess: (d) => { qc.invalidateQueries({ queryKey: ["admin-reseller", id] }); toast.success(d?.status === "pending_approval" ? "Piano creato: il rivenditore firmerà il modulo integrativo" : "Piano creato e attivo"); },
    onError: (e) => toast.error(e.response?.data?.detail || "Errore"),
  });

  if (isLoading) return <div className="text-slate-500">Caricamento…</div>;
  if (!r) return <div>Non trovato</div>;

  const productMap = Object.fromEntries(products.map((p) => [p.id, p]));

  return (
    <div className="space-y-6">
      <Link to="/admin/resellers" className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-[#B84A2B]"><ArrowLeft size={14} /> Rivenditori</Link>
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight">{r.company?.ragione_sociale || r.email}</h1>
          <div className="mt-1 text-sm text-slate-500 flex items-center gap-3">
            <span className="font-mono">{r.codice_reseller}</span>
            <span>·</span>
            <span>Creato il {formatDate(r.created_at)}</span>
          </div>
        </div>
        <Button
          data-testid="btn-block-toggle"
          onClick={() => toggleBlock.mutate(!r.blocked)}
          className={r.blocked ? "bg-emerald-700 hover:bg-emerald-800 text-white" : "bg-slate-900 hover:bg-slate-800 text-white"}
        >
          {r.blocked ? <><Unlock size={14} className="mr-1" /> Sblocca accesso</> : <><Lock size={14} className="mr-1" /> Blocca accesso</>}
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="bg-white border border-[#E2DDD5] rounded-xl p-5">
          <div className="font-semibold mb-3">Dati contabili</div>
          <dl className="text-sm grid grid-cols-2 gap-y-2">
            <dt className="text-slate-500">P. IVA</dt><dd className="font-mono">{r.company?.piva || "—"}</dd>
            <dt className="text-slate-500">Codice Fiscale</dt><dd className="font-mono">{r.company?.codice_fiscale || "—"}</dd>
            <dt className="text-slate-500">Regime fiscale</dt><dd>{r.company?.regime_fiscale || "—"}</dd>
            <dt className="text-slate-500">IBAN</dt><dd className="font-mono text-xs">{r.company?.iban || "—"}</dd>
            <dt className="text-slate-500">Sede</dt><dd>{r.company?.sede || "—"}</dd>
          </dl>
        </div>
        <div className="bg-white border border-[#E2DDD5] rounded-xl p-5">
          <div className="font-semibold mb-3">Documentazione</div>
          <div className="text-sm space-y-2">
            <div className="flex items-center justify-between"><span>Documenti anagrafici</span><span className={r.docs_approved ? "text-emerald-700" : "text-amber-700"}>{r.docs_approved ? "Approvati" : "In attesa"}</span></div>
            <div className="flex items-center justify-between"><span>Accordo GoWeb-Rivenditore</span><span className={r.agreement_approved ? "text-emerald-700" : "text-amber-700"}>{r.agreement_approved ? "Approvato" : "In attesa"}</span></div>
          </div>
          {r.documents?.length > 0 && (
            <div className="mt-4 pt-4 border-t border-[#E2DDD5] space-y-2 text-sm">
              {r.documents.map((d) => (
                <div key={d.id} className="flex items-center justify-between">
                  <div className="truncate">{KIND[d.kind] || d.kind} · {d.original_filename} <span className="text-xs text-slate-500">({label(d.status)})</span></div>
                  <a href="#" onClick={async (e) => { e.preventDefault(); const resp = await api.get(`/files/${d.id}/download`, { responseType: "blob" }); const url = URL.createObjectURL(resp.data); window.open(url, "_blank"); }} className="text-[#B84A2B] hover:underline text-xs flex items-center gap-1"><FileDown size={12} /> Apri</a>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="bg-white border border-[#E2DDD5] rounded-xl p-5">
        <div className="font-semibold mb-3">Prodotti assegnati</div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
          {products.map((p) => (
            <label key={p.id} className="flex items-center gap-2 p-2 border border-[#E2DDD5] rounded-lg hover:bg-[#F9F8F6]" data-testid={`assign-product-${p.id}`}>
              <Checkbox
                checked={effectiveIds.includes(p.id)}
                onCheckedChange={(v) => {
                  const next = v ? [...effectiveIds, p.id] : effectiveIds.filter((x) => x !== p.id);
                  setSelectedIds(next);
                }}
              />
              <div>
                <div className="text-sm font-medium">{p.name}</div>
                <div className="text-xs text-slate-500">{p.modules.length} moduli</div>
              </div>
            </label>
          ))}
        </div>
        <div className="mt-4">
          <Button onClick={() => saveAssignments.mutate()} className="bg-[#B84A2B] hover:bg-[#983C21] text-white" data-testid="btn-save-assignments">Salva assegnazioni</Button>
        </div>
      </div>

      <div className="bg-white border border-[#E2DDD5] rounded-xl p-5">
        <div className="font-semibold mb-3">Piani provvigionali</div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-xs uppercase tracking-wider text-slate-500 bg-[#F2F0EB]">
              <tr><th className="text-left px-3 py-2">Prodotto</th><th className="text-left px-3 py-2">Tipo</th><th className="text-left px-3 py-2">Dettaglio</th><th className="text-left px-3 py-2">Stato</th><th className="text-left px-3 py-2">Decorrenza</th></tr>
            </thead>
            <tbody>
              {(r.plans || []).map((p) => (
                <tr key={p.id} className="border-t border-[#E2DDD5]">
                  <td className="px-3 py-2">{productMap[p.product_id]?.name || p.product_id}</td>
                  <td className="px-3 py-2">{label(p.type)}</td>
                  <td className="px-3 py-2 font-mono text-xs">
                    {p.type === "percentage" && `${p.config.percent}%`}
                    {p.type === "fixed" && formatEUR(p.config.amount) + (Object.entries(p.config.module_amounts || {}).filter(([, v]) => v).map(([k, v]) => ` + ${productMap[p.product_id]?.modules?.find((m) => m.id === k)?.name || "modulo"} ${formatEUR(v)}`).join(""))}
                    {p.type === "tiered" && p.config.tiers?.map((t, i) => `[${t.from}-${t.to ?? "∞"}]:${t.percent}%`).join(" ")}
                  </td>
                  <td className="px-3 py-2">{label(p.status)}</td>
                  <td className="px-3 py-2 text-slate-500">{formatDate(p.effective_from)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="mt-5 pt-5 border-t border-[#E2DDD5] grid grid-cols-1 md:grid-cols-5 gap-3 items-end">
          <div className="md:col-span-2">
            <Label>Prodotto</Label>
            <Select value={newPlan.product_id} onValueChange={(v) => setNewPlan({ ...newPlan, product_id: v })}>
              <SelectTrigger data-testid="select-plan-product"><SelectValue placeholder="Scegli prodotto" /></SelectTrigger>
              <SelectContent>
                {products.map((p) => (<SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Tipo</Label>
            <Select value={newPlan.type} onValueChange={(v) => setNewPlan({ ...newPlan, type: v })}>
              <SelectTrigger data-testid="select-plan-type"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="percentage">Percentuale</SelectItem>
                <SelectItem value="fixed">Importo fisso</SelectItem>
                <SelectItem value="tiered">A scaglioni</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {newPlan.type === "percentage" && (
            <div><Label>%</Label><Input type="number" value={newPlan.percent} onChange={(e) => setNewPlan({ ...newPlan, percent: e.target.value })} data-testid="input-plan-percent" /></div>
          )}
          {newPlan.type === "fixed" && (
            <div><Label>€ per cliente</Label><Input type="number" value={newPlan.amount} onChange={(e) => setNewPlan({ ...newPlan, amount: e.target.value })} data-testid="input-plan-amount" /></div>
          )}
          {newPlan.type === "tiered" && (
            <div className="md:col-span-2 text-xs text-slate-500 border border-dashed border-[#E2DDD5] p-2 rounded">
              Scaglioni di default: 0–10 → 20%, 11+ → 25%. Modificabili via API.
            </div>
          )}
          <div>
            <Button onClick={() => createPlan.mutate()} disabled={!newPlan.product_id} className="bg-[#B84A2B] hover:bg-[#983C21] text-white w-full" data-testid="btn-create-plan"><FilePlus size={14} className="mr-1" /> Crea piano</Button>
          </div>
        </div>
        {newPlan.type === "fixed" && newPlan.product_id && (
          <div className="mt-3 grid grid-cols-1 md:grid-cols-4 gap-3" data-testid="plan-module-amounts">
            {(productMap[newPlan.product_id]?.modules || []).map((m) => (
              <div key={m.id}>
                <Label className="text-xs">+ € per modulo «{m.name}»</Label>
                <Input type="number" min="0" value={newPlan.module_amounts[m.id] ?? ""} placeholder="0" onChange={(e) => setNewPlan({ ...newPlan, module_amounts: { ...newPlan.module_amounts, [m.id]: e.target.value } })} data-testid={`input-plan-module-${m.id}`} />
              </div>
            ))}
          </div>
        )}
        <div className="mt-2 text-xs text-slate-500">La creazione di un piano quando ne esiste già uno attivo genera un modulo integrativo da far firmare al rivenditore. Fino all'approvazione l'accesso sarà in sola lettura.</div>
      </div>

      <div className="bg-white border border-[#E2DDD5] rounded-xl p-5">
        <div className="font-semibold mb-3">Clienti del rivenditore</div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-xs uppercase tracking-wider text-slate-500 bg-[#F2F0EB]">
              <tr><th className="text-left px-3 py-2">Cliente</th><th className="text-left px-3 py-2">Prodotto</th><th className="text-left px-3 py-2">Stato</th><th className="text-left px-3 py-2">Licenza fino al</th></tr>
            </thead>
            <tbody>
              {(r.customers || []).map((c) => (
                <tr key={c.id} className="border-t border-[#E2DDD5]">
                  <td className="px-3 py-2">{c.dati_fiscali?.ragione_sociale || `${c.anagrafica.nome} ${c.anagrafica.cognome}`}</td>
                  <td className="px-3 py-2">{productMap[c.product_id]?.name || c.product_id}</td>
                  <td className="px-3 py-2">{label(c.status)}</td>
                  <td className="px-3 py-2 text-slate-500">{formatDate(c.license_end)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
